import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
  IconographyThemes,
  InlineEditor,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import IToolBarConfig from "@hid-galaxy-ui/galaxy-react/components/InlineEditor/IToolbarConfig";
import { addPartnerTierListPayloadType } from "../../../../../../../redux/saga/partner-program/saga-actions";
import { useAppSelector } from "../../../../../../../redux";
import "./PartnerTierModal.scss";

type BenefitGroups = {
  [categoryName: string]: string[];
};

type Props = {
  onClose: () => void;
  onSubmit: (payload: addPartnerTierListPayloadType) => void;
  open?: boolean;
  mode?: "add" | "edit" | "view";
  initialValue?: Partial<addPartnerTierListPayloadType> & {
    benefits?: BenefitGroups;
  };
  busy?: boolean;
};

const fieldSx = {
  "& .MuiFilledInput-root": {
    backgroundColor: "#F7F7F7",
    borderRadius: "8px",
    border: "1px solid #E6E6E6",
    overflow: "hidden",
    "&:before, &:after": { display: "none" },
    minHeight: 36,
    "& .MuiInputBase-input": {
      padding: "8px 12px",
      fontSize: "14px",
      lineHeight: "20px",
      color: "#222",
      "&::placeholder": { color: "#9E9E9E", opacity: 1, fontSize: "10px" },
    },
    "&:hover": {
      backgroundColor: "#F7F7F7",
      borderColor: "#BDBDBD",
    },
    "&.Mui-focused": {
      borderColor: "#BDBDBD",
      boxShadow: "none",
      backgroundColor: "#F7F7F7",
    },
    "&.Mui-disabled": {
      backgroundColor: "#F5F5F5",
      borderColor: "#E0E0E0",
      "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#9E9E9E" },
    },
  },
  "& .MuiInputLabel-root": {
    color: "#737373",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "21px",
    letterSpacing: "0.2px",
    marginBottom: "4px",
    transform: "none",
    position: "relative",
  },
  "& .MuiInputLabel-shrink": { transform: "none" },
  "& .MuiFormHelperText-root": { marginTop: "6px", fontSize: "12px" },
};

const tierInlineEditorToolBarConfig: IToolBarConfig = {
  headingConfig: {
    isDisplayHeading: true,
    headings: [false, 1, 2, 3, 4, 5, 6],
  },
  fontStyleConfig: {
    isDisplayBold: true,
    isDisplayItalic: true,
    isDisplayUnderline: true,
    isDisplayStrike: true,
  },
  indentConfig: {
    isDisplayOrdered: true,
    isDisplayBullet: true,
    isDisplayIndent: false,
  },
  colorConfig: {
    isDisplayBackground: false,
    isDisplayColor: false,
  },
  alignmentConfig: {
    isDisplayJustify: false,
    isDisplayCenter: false,
    isDisplayRight: false,
  },
  miscConfig: {
    isDisplayImage: false,
    isDisplayLink: false,
    isDisplayCodeblock: false,
    isDisplayBlockquote: false,
  },
  buttonConfig: {
    buttonLabel: "",
    displayPostButton: false,
    isRequired: false,
  },
};

const PartnerTierModal: React.FC<Props> = ({
  onClose,
  onSubmit,
  open = true,
  mode = "add",
  initialValue,
  busy = false,
}) => {
  const [tab, setTab] = React.useState<number>(0);
  const isView = mode === "view";

  const defaultMin =
    mode === "add" && initialValue?.min_points == null
      ? null
      : initialValue?.min_points == null ? null : Number(initialValue?.min_points ?? 0);
  const defaultMax =
    mode === "add" && initialValue?.max_points == null
      ? null
      : initialValue?.max_points == null ? null : Number(initialValue?.max_points ?? 0);

  const [form, setForm] = React.useState<addPartnerTierListPayloadType>({
    ID: initialValue?.ID ?? 0,
    tier_name: initialValue?.tier_name ?? "",
    min_points: defaultMin,
    max_points: defaultMax,
    short_description: initialValue?.short_description ?? "",
    long_description: initialValue?.long_description ?? "",
    created_at: initialValue?.created_at ?? "",
    update_at: initialValue?.update_at ?? "",
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [minPointsInput, setMinPointsInput] = React.useState<string>(
    defaultMin === null || defaultMin === undefined ? "" : (defaultMin === 0 ? "0" : defaultMin.toString())
  );
  const [maxPointsInput, setMaxPointsInput] = React.useState<string>(
    defaultMax === null || defaultMax === undefined ? "" : defaultMax.toString()
  );

  const {
    status: {
      addPartnerTierStatus,
      addPartnerTierLoading,
      addPartnerTierMessage,
      updatePartnerTierLoading,
      updatePartnerTierStatus,
      updatePartnerTierMessage,
    },
  } = useAppSelector((state) => state.partner_program);

  React.useEffect(() => {
    if (!open) return;

    const min =
      mode === "add" && initialValue?.min_points == null
        ? null
        : initialValue?.min_points == null ? null : Number(initialValue?.min_points ?? 0);
    const max =
      mode === "add" && initialValue?.max_points == null
        ? null
        : initialValue?.max_points == null ? null : Number(initialValue?.max_points ?? 0);

    setForm({
      ID: initialValue?.ID ?? 0,
      tier_name: initialValue?.tier_name ?? "",
      min_points: min,
      max_points: max,
      short_description: initialValue?.short_description ?? "",
      long_description: initialValue?.long_description ?? "",
      created_at: initialValue?.created_at ?? "",
      update_at: initialValue?.update_at ?? "",
    });
    setMinPointsInput(min === null || min === undefined ? "" : (min === 0 ? "0" : min.toString()));
    setMaxPointsInput(max === null || max === undefined ? "" : max.toString());
    setErrors({});
    setTab(0);
  }, [open, initialValue, mode]);

  const validate = () => {
    if (isView) return true;
    const e: Record<string, string> = {};

    if (!form.tier_name.trim()) e.tier_name = "Tier name is required";

    const min = form.min_points === null || form.min_points === undefined ? null : Number(form.min_points);
    const max = form.max_points === null || form.max_points === undefined ? null : Number(form.max_points);
    if (min === null || !Number.isFinite(min) || min < 0)
      e.min_points = "Min points is required and must be a non-negative number";
    if (max !== null && (!Number.isFinite(max) || max < 0))
      e.max_points = "Max points must be a non-negative number";
    if (min !== null && Number.isFinite(min) && max !== null && Number.isFinite(max) && min > max) {
      e.max_points = "Max points should be greater than or equal to min points";
    }

    if (!form.long_description.trim()) {
      e.long_description = "Description is required";
    } else if (form.long_description.length > 1000) {
      e.long_description = "Description must be at most 1000 characters";
    }

    if (form.short_description.trim() && form.short_description.length > 500) {
      e.short_description = "Comment must be at most 500 characters";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitted(true);
    if (isView) {
      onClose();
      return;
    }

    const payload: addPartnerTierListPayloadType = {
      ID: form.ID,
      tier_name: form.tier_name.trim(),
      min_points: form.min_points === null || form.min_points === undefined ? (null as any) : Number(form.min_points),
      max_points: form.max_points === null || form.max_points === undefined ? (null as any) : Number(form.max_points),
      short_description: form.short_description.trim(),
      long_description: form.long_description.trim(),
      created_at: form.created_at || new Date().toISOString(),
      update_at: new Date().toISOString(),
    };

    onSubmit(payload);
  };

  const title = isView
    ? `Tier - ${form.tier_name || initialValue?.tier_name || ""}`
    : mode === "edit"
    ? "Edit New Tier"
    : "Add New Tier";

  const subtitle = isView
    ? undefined
    : "Configure a new partner tier with point ranges";

  const hasAnyBenefits = Object.values(initialValue?.benefits ?? {}).some(
    (benefitNames) => (benefitNames?.length ?? 0) > 0
  );

  const tabs = isView
    ? hasAnyBenefits
      ? ["Description and Comment", "Benefits"]
      : ["Description and Comment"]
    : ["Point Ranges", "Description and Comment"];

  const showPrimaryButton = !isView;
  const ctaLabel = mode === "edit" ? "SAVE CHANGES" : "ADD TIER";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="tier-modal"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {(!submitted || addPartnerTierLoading || updatePartnerTierLoading) && (
        <DialogTitle className="tier-modal-title">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography className="title-text">{title}</Typography>

              {!isView && (
                <Typography className="subtitle-text">{subtitle}</Typography>
              )}
            </Box>

            <IconButton aria-label="Close" onClick={onClose}>
              <Iconography icon="xmark" />
            </IconButton>
          </Box>
        </DialogTitle>
      )}
      {submitted ? (
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
          style={{ padding: "24px" }}
        >
          {(addPartnerTierLoading || updatePartnerTierLoading) && (
            <Loader variant={LoaderSizes.medium} />
          )}
          {mode === "add" && !addPartnerTierLoading && (
            <>
              <Iconography
                icon={addPartnerTierStatus ? "circleCheck" : "circleXmark"}
                size={IconographySizes.XLarge}
                theme={
                  addPartnerTierStatus
                    ? IconographyThemes.Success
                    : IconographyThemes.Error
                }
              />
              <Typography variant="body2">
                {addPartnerTierMessage ||
                  (addPartnerTierStatus
                    ? "Tier added successfully"
                    : "Failed to add tier")}
              </Typography>
              <Button label="Close" onClick={onClose} variant="primary" />
            </>
          )}
          {mode === "edit" && !updatePartnerTierLoading && (
            <>
              <Iconography
                icon={updatePartnerTierStatus ? "circleCheck" : "circleXmark"}
                size={IconographySizes.XLarge}
                theme={
                  updatePartnerTierStatus
                    ? IconographyThemes.Success
                    : IconographyThemes.Error
                }
              />
              <Typography variant="body2">
                {updatePartnerTierMessage ||
                  (updatePartnerTierStatus
                    ? "Tier updated successfully"
                    : "Failed to update tier")}
              </Typography>
              <Button label="Close" onClick={onClose} variant="primary" />
            </>
          )}
        </DialogContent>
      ) : (
        <>
          <DialogContent className="tier-modal-content">
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="Tier Modal Tabs"
              variant="scrollable"
              scrollButtons="auto"
              className="tabs-container"
            >
              {tabs.map((label) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>

            {isView && tab === 0 && (
              <Box className="view-section">
                <Typography variant="subtitle2" className="section-title">
                  Description
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: form.long_description?.trim() || "—",
                  }}
                />

                <Box className="comment-box">
                  <Typography variant="h1" className="section-title">
                    Comments
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: form.short_description?.trim() || "—",
                    }}
                  />
                </Box>
              </Box>
            )}

            {isView && tab === 1 && (
              <Box className="benefits-grid">
                {Object.entries(initialValue?.benefits ?? {}).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No benefits configured for this tier.
                  </Typography>
                ) : (
                  Object.entries(initialValue?.benefits ?? {}).map(
                    ([categoryName, benefitNames]) => (
                      <Box className="benefit-section" key={categoryName}>
                        <Typography variant="subtitle2" className="benefit-title">
                          {categoryName}
                        </Typography>
                        {(benefitNames?.length ?? 0) === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No benefits mapped in this category.
                          </Typography>
                        ) : (
                          <Box className="benefit-list">
                            {benefitNames.map((benefitName) => (
                              <Typography
                                key={`${categoryName}-${benefitName}`}
                                variant="body2"
                                color="text.secondary"
                              >
                                {benefitName}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )
                  )
                )}
              </Box>
            )}

            {!isView && tab === 0 && (
              <Box>
                <FormControl
                  fullWidth
                  variant="filled"
                  sx={fieldSx}
                  className="field-with-spacing"
                >
                  <InputLabel htmlFor="tier-name">Tier Name *</InputLabel>
                  <TextField
                    id="tier-name"
                    placeholder="Enter tier name"
                    value={form.tier_name}
                    onChange={(e) =>
                      setForm({ ...form, tier_name: e.target.value })
                    }
                    InputProps={{ disableUnderline: true }}
                    error={!!errors.tier_name}
                    helperText={errors.tier_name || ""}
                    fullWidth
                  />
                </FormControl>

                <Box className="form-grid-two-col">
                  <FormControl fullWidth variant="filled" sx={fieldSx}>
                    <InputLabel htmlFor="min-points">Min Points *</InputLabel>
                    <TextField
                      id="min-points"
                      type="text"
                      value={minPointsInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        // Only allow digits
                        const digitsOnly = raw.replace(/\D/g, "");
                        setMinPointsInput(digitsOnly);
                        setForm({
                          ...form,
                          min_points:
                            digitsOnly === "" ? null : Number(digitsOnly),
                        });
                      }}
                      onBlur={() => {
                        // Keep empty string as null (don't convert to 0)
                        if (minPointsInput === "") {
                          setForm({ ...form, min_points: null });
                        }
                      }}
                      InputProps={{ disableUnderline: true }}
                      inputProps={{ min: 0 }}
                      error={!!errors.min_points}
                      helperText={errors.min_points || ""}
                      placeholder="1000"
                      fullWidth
                    />
                  </FormControl>

                  <FormControl fullWidth variant="filled" sx={fieldSx}>
                    <InputLabel htmlFor="max-points">Max Points</InputLabel>
                    <TextField
                      id="max-points"
                      type="text"
                      value={maxPointsInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        // Only allow digits
                        const digitsOnly = raw.replace(/\D/g, "");
                        setMaxPointsInput(digitsOnly);
                        setForm({
                          ...form,
                          max_points:
                            digitsOnly === "" ? null : Number(digitsOnly),
                        });
                      }}
                      onBlur={() => {
                        // Keep empty string as null (don't convert to 0)
                        if (maxPointsInput === "") {
                          setForm({ ...form, max_points: null });
                        }
                      }}
                      InputProps={{ disableUnderline: true }}
                      inputProps={{ min: 0 }}
                      error={!!errors.max_points}
                      helperText={errors.max_points || ""}
                      placeholder="4999"
                      fullWidth
                    />
                  </FormControl>
                </Box>
              </Box>
            )}

            {!isView && tab === 1 && (
              <Box>
                <Box className="field-with-spacing">
                  <Box className="field-label-row">
                    <label>
                      Description <span className="required">*</span>
                    </label>
                    <span className="char-count">
                      {(form.long_description || "").length}/1000
                    </span>
                  </Box>
                  <Box className="tier-inline-editor-wrap">
                    <InlineEditor
                      id="tier-description"
                      placeholder="Enter description"
                      toolBarConfig={tierInlineEditorToolBarConfig}
                      editorData={form.long_description}
                      handleOnChange={(value: string) =>
                        setForm((prev) => ({
                          ...prev,
                          long_description:
                            value.length <= 1000 ? value : prev.long_description,
                        }))
                      }
                    />
                  </Box>
                  {errors.long_description && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {errors.long_description}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box className="field-label-row">
                    <label>Comment</label>
                    <span className="char-count">
                      {(form.short_description || "").length}/500
                    </span>
                  </Box>
                  <Box className="tier-inline-editor-wrap tier-inline-editor-wrap--comment">
                    <InlineEditor
                      id="tier-comment"
                      placeholder="Enter comment"
                      toolBarConfig={tierInlineEditorToolBarConfig}
                      editorData={form.short_description}
                      handleOnChange={(value: string) =>
                        setForm((prev) => ({
                          ...prev,
                          short_description:
                            value.length <= 500 ? value : prev.short_description,
                        }))
                      }
                    />
                  </Box>
                  {errors.short_description && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {errors.short_description}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            {tab === 0 ? (
              <Button variant="secondary" label="CANCEL" onClick={onClose} />
            ) : (
              <Button
                variant="secondary"
                label="Back"
                onClick={() => setTab(tab - 1)}
                disabled={busy}
              />
            )}
            {showPrimaryButton && (
              <>
                {tab === 1 ? (
                  <Button
                    variant="primary"
                    label="Save"
                    onClick={handleSubmit}
                    disabled={busy}
                    loading={busy}
                  />
                ) : (
                  <Button
                    variant="primary"
                    label="Next"
                    onClick={() => {
                      if (tab === 0) {
                        // Validate Point Ranges tab
                        const e: Record<string, string> = {};
                        if (!form.tier_name.trim())
                          e.tier_name = "Tier name is required";
                        const min = form.min_points === null || form.min_points === undefined ? null : Number(form.min_points);
                        const max = form.max_points === null || form.max_points === undefined ? null : Number(form.max_points);
                        if (min === null || !Number.isFinite(min) || min < 0)
                          e.min_points =
                            "Min points is required and must be a non-negative number";
                        if (max !== null && (!Number.isFinite(max) || max < 0))
                          e.max_points =
                            "Max points must be a non-negative number";
                        if (
                          min !== null &&
                          Number.isFinite(min) &&
                          max !== null &&
                          Number.isFinite(max) &&
                          min > max
                        ) {
                          e.max_points =
                            "Max points should be greater than or equal to min points";
                        }
                        setErrors(e);
                        if (Object.keys(e).length === 0) setTab(tab + 1);
                      } else if (tab === 1) {
                        // Validate Description and Comment tab
                        const e: Record<string, string> = {};
                        if (!form.long_description.trim()) {
                          e.long_description = "Description is required";
                        } else if (form.long_description.length > 1000) {
                          e.long_description =
                            "Description must be at most 1000 characters";
                        }
                        if (form.short_description.trim() && form.short_description.length > 500) {
                          e.short_description =
                            "Comment must be at most 500 characters";
                        }
                        setErrors(e);
                        if (Object.keys(e).length === 0) setTab(tab + 1);
                      } else {
                        setTab(tab + 1);
                      }
                    }}
                  />
                )}
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PartnerTierModal;
